import { useState } from 'react';
import { Palette, Type, Maximize2, Square, Box, Layout, ChevronDown, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TokenInput } from './TokenInput';

interface TokenDefinition {
  label: string;
  description?: string;
  category: string;
  editable: string;
  type: 'color' | 'fontFamily' | 'size' | 'shadow' | 'number';
  default: string | number;
  control?: {
    type: 'slider' | 'dropdown' | 'preset';
    options?: Array<{ value: string; label: string }>;
    min?: number;
    max?: number;
    step?: number;
  };
}

interface CategorySectionProps {
  category: string;
  tokens: Array<[string, TokenDefinition]>;
  defaults: Record<string, string | number>;
  onChange: (tokenId: string, value: string) => void;
  readOnly?: boolean;
}

interface CategoryConfig {
  icon: LucideIcon;
  label: string;
  description: string;
}

const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  colors: {
    icon: Palette,
    label: 'Colori',
    description: 'Palette cromatica e identità visiva',
  },
  typography: {
    icon: Type,
    label: 'Tipografia',
    description: 'Font, dimensioni e spaziature del testo',
  },
  spacing: {
    icon: Maximize2,
    label: 'Spaziatura',
    description: 'Margini e padding tra elementi',
  },
  borders: {
    icon: Square,
    label: 'Bordi',
    description: 'Arrotondamenti e spessori',
  },
  shadows: {
    icon: Box,
    label: 'Ombre',
    description: 'Elevazione e profondità',
  },
  layout: {
    icon: Layout,
    label: 'Layout',
    description: 'Larghezza massima e griglie',
  },
};

export function CategorySection({
  category,
  tokens,
  defaults,
  onChange,
  readOnly = false,
}: CategorySectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const config = CATEGORY_CONFIG[category] || {
    icon: Box,
    label: category,
    description: '',
  };

  const Icon = config.icon;
  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <div className="category-section mb-8">
      {/* Category Header - Clickable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start space-x-3 mb-4 pb-3 border-b border-gray-200 hover:bg-gray-50 transition-colors rounded-t-lg px-2 py-2 -mx-2 text-left"
      >
        <ChevronIcon className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
        <div className="p-2 bg-primary-100 rounded-lg">
          <Icon className="w-5 h-5 text-primary-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{config.label}</h3>
          <p className="text-sm text-gray-600">{config.description}</p>
        </div>
        <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0">
          {tokens.length} {tokens.length === 1 ? 'token' : 'tokens'}
        </div>
      </button>

      {/* Tokens Grid - Collapsible */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tokens.map(([id, definition]) => (
          <div
            key={id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            <TokenInput
              id={id}
              definition={definition}
              value={defaults[id] ?? definition.default}
              onChange={(newValue) => onChange(id, newValue)}
              readOnly={readOnly}
            />
          </div>
        ))}
        </div>
      )}
    </div>
  );
}
